import { useTranslation } from 'react-i18next';
import { Eye } from 'lucide-react';
import { EMOJI_MAP } from './ReactionButton';
import type { ReactionType } from '../types';

interface ReactionSummaryProps {
  reactionCounts: Record<ReactionType, number>;
  commentCount: number;
  viewCount: number;
  onCommentClick: () => void;
}

export function ReactionSummary({
  reactionCounts,
  commentCount,
  viewCount,
  onCommentClick,
}: ReactionSummaryProps) {
  // Get top 3 reactions sorted by count (only those with count > 0)
  const topReactions = (Object.entries(reactionCounts) as [ReactionType, number][])
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const totalReactions = Object.values(reactionCounts).reduce((sum, c) => sum + c, 0);

  const { t } = useTranslation();

  if (totalReactions === 0 && commentCount === 0) return null;

  return (
    <div className="flex items-center justify-between px-4 py-1">
      {/* Left: Overlapping emoji circles + count */}
      <div className="flex items-center gap-1.5">
        {topReactions.length > 0 && (
          <div className="flex items-center -space-x-1 rtl:space-x-reverse">
            {topReactions.map(([type]) => (
              <span
                key={type}
                className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-xs leading-none shadow-sm"
                style={{ border: '1.5px solid #CED0D4' }}
              >
                {EMOJI_MAP[type]}
              </span>
            ))}
          </div>
        )}
        {totalReactions > 0 && (
          <span className="text-[13px] font-normal" style={{ color: '#65676B' }}>
            {totalReactions}
          </span>
        )}
      </div>

      {/* Right: Comment count + view count */}
      <div className="flex items-center gap-3">
        {commentCount > 0 && (
          <button
            onClick={onCommentClick}
            className="text-[13px] hover:underline"
            style={{ color: '#65676B' }}
          >
            {t('feed.comments_count', { count: commentCount })}
          </button>
        )}
        <span className="flex items-center gap-1 text-[13px]" style={{ color: '#65676B' }}>
          <Eye className="h-3.5 w-3.5" />
          {viewCount}
        </span>
      </div>
    </div>
  );
}
